import { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import useGlobalContext from "../../context/useGlobalContext";
import UserInteractionsService from "../../services/interactions/Interactions";
import { normalizeToFullDay, useDebounce } from "../../../src/utils/utils";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import cameraIcon from "../../assets/action-items/camera.svg";
import searchIcon from "../../assets/action-items/search.svg";
import editIcon from "../../assets/action-items/edit.svg";
import deleteIcon from "../../assets/action-items/delete.svg";
import { useNavigate } from "react-router-dom";
import "../../styles/actionItems.css";

interface InteractionProps {
  interactionId: string | null | undefined;
  interactionTitle: string | null | undefined;
  duration: number | null | undefined;
  onSelectInteraction: (
    id: string,
    details?: {
      interactionTitle?: string | null;
      startTime?: string | null;
      duration?: number | null;
    }
  ) => void;
  startTime?: string | null | undefined;
  canEdit: boolean | undefined;
}

const LinkedInteraction = (props: InteractionProps) => {
  const { accountDetails } = useGlobalContext();
  const loggedInProfileOrgId = accountDetails?.profileOrgId;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const getCurrentWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  };

  const addDays = (date: Date, days: number) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  };

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });

  const [weekStart, setWeekStart] = useState<Date>(() => getCurrentWeekStart());
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const [queryDateRange, setQueryDateRange] = useState<{
    startUTC: string;
    endUTC: string;
  }>(() => {
    const currentWeekStart = getCurrentWeekStart();
    const { startDate, endDate } = normalizeToFullDay(
      currentWeekStart,
      addDays(currentWeekStart, 6)
    );
    return {
      startUTC: startDate.toISOString(),
      endUTC: endDate.toISOString(),
    };
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const isSameWeek = (date1: Date, date2: Date) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    d1.setDate(d1.getDate() - d1.getDay());
    d2.setDate(d2.getDate() - d2.getDay());
    return d1.toDateString() === d2.toDateString();
  };

  const isCurrentWeek = isSameWeek(weekStart, new Date());

  const goToPreviousWeek = () => {
    setWeekStart((prev) => addDays(prev, -7));
  };

  const goToNextWeek = () => {
    if (!isCurrentWeek) {
      setWeekStart((prev) => addDays(prev, 7));
    }
  };

  useEffect(() => {
    const { startDate, endDate } = normalizeToFullDay(weekStart, weekEnd);
    setQueryDateRange({
      startUTC: startDate.toISOString(),
      endUTC: endDate.toISOString(),
    });
  }, [weekStart, weekEnd]);

  const formatDisplayDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const shouldCallApi =
    !!loggedInProfileOrgId && debouncedSearch.trim().length > 0;

  const {
    isLoading,
    isFetching,
    isSuccess,
    data: interactionsData,
  } = UserInteractionsService.useGetUserInteractions(
    {
      startTime: queryDateRange.startUTC,
      endTime: queryDateRange.endUTC,
      offset: 1,
      limit: 100,
      interactionTitle: debouncedSearch || "",
      isExternal: false,
      sortBy: "start_time",
      sortOrder: "desc",
      isTeamInteraction: true,
      isTeamOnlyInteraction: true,
      employeePorId: loggedInProfileOrgId!,
      searchValue: debouncedSearch || "",
      isIncludeMeInTeamInteraction: true,
    },
    shouldCallApi,
  );
  const [interactions, setInteractions] = useState<any[]>([]);

  useEffect(() => {
    if (!shouldCallApi) {
      setInteractions([]);
      return;
    }
    if (isSuccess) {
      setInteractions(interactionsData?.data || []);
    }
  }, [isSuccess, interactionsData, shouldCallApi]);

  const isSearching = debouncedSearch.trim().length > 0;

  const [isEditing, setIsEditing] = useState(false);

  // Locally track the selected interaction so read mode immediately shows
  // the newly picked item without waiting for props to update from parent.
  const [selectedInteraction, setSelectedInteraction] = useState<{
    interactionId: string;
    interactionTitle: string;
    startTime: string;
    duration: number;
  } | null>(null);

  // Track whether the last interactionId change was triggered by us (handleClick)
  // so we don't wipe selectedInteraction when the parent propagates our own selection back.
  const selfSelectedIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const next = props.interactionId;
    if (!next) {
      selfSelectedIdRef.current = undefined;
      setSelectedInteraction(null);
      return;
    }
    // Keep local details when parent propagates the same linked interaction id.
    if (next === selfSelectedIdRef.current) return;
    if (selectedInteraction?.interactionId === next) return;
    // Different non-empty id means parent switched context/item.
    selfSelectedIdRef.current = undefined;
    setSelectedInteraction(null);
  }, [props.interactionId, selectedInteraction?.interactionId]);

  // Show read mode when we have a real id from props OR a locally selected interaction.
  const hasInteraction = !!(props.interactionId || selectedInteraction?.interactionId);

  const isRequestFinished = isSuccess && !isLoading && !isFetching;

  // Read-mode display values: prefer local selection, fall back to props.
  const displayTitle =
    selectedInteraction?.interactionTitle ?? props.interactionTitle;
  const displayDate = selectedInteraction
    ? formatDisplayDate(selectedInteraction.startTime)
    : props.startTime
      ? formatDisplayDate(props.startTime)
      : "";
  const displayDuration = selectedInteraction?.duration ?? props.duration;
  const formatDuration = (totalMinutes?: number | null) => {
    if (!totalMinutes && totalMinutes !== 0) return "";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours} hr${hours > 1 ? "s" : ""}${
        minutes > 0 ? ` ${minutes} min` : ""
      }`;
    }

    return `${minutes} min`;
  };

  const formattedDuration = formatDuration(displayDuration);

  const handleClick = (interaction: any) => {
    const nextInteractionId =
      interaction?.interactionId || interaction?.id || interaction?.interaction_id;
    const nextInteractionTitle =
      interaction?.interactionTitle ||
      interaction?.title ||
      interaction?.interaction_title ||
      "Untitled";
    const nextInteractionStartTime =
      interaction?.startTime ||
      interaction?.start_time ||
      interaction?.meetingStartTime ||
      "";
    const rawDuration =
      interaction?.duration ??
      interaction?.durationInMinutes ??
      interaction?.meetingDuration ??
      interaction?.duration_minutes;
    const nextInteractionDuration =
      typeof rawDuration === "number" ? rawDuration : Number(rawDuration) || 0;

    if (!nextInteractionId) return;

    // Record the id we're about to select so the useEffect above won't clear it.
    selfSelectedIdRef.current = nextInteractionId;
    props.onSelectInteraction(nextInteractionId, {
      interactionTitle: nextInteractionTitle,
      startTime: nextInteractionStartTime,
      duration: nextInteractionDuration,
    });
    setSelectedInteraction({
      interactionId: nextInteractionId,
      interactionTitle: nextInteractionTitle,
      startTime: nextInteractionStartTime,
      duration: nextInteractionDuration,
    });
    setSearch("");
    setIsEditing(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (isEditing) {
          setIsEditing(false);
          setSearch("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && props.interactionTitle) {
      setSearch(props.interactionTitle);
    }
  }, [isEditing]);

  return (
    <Box ref={wrapperRef}>
      {hasInteraction && !isEditing ? (
        <Box className="linked-interaction-read-mode">
          <Box className="linked-interaction-read-content">
            <Box
              component="img"
              src={cameraIcon}
              alt="camera"
              className="linked-interaction-camera-icon"
            />
            <Typography
              onClick={() =>
                navigate(`/meeting/${props.interactionId}`, {
                  state: {
                    parent_page_identifier: "action-items",
                  },
                })
              }
              className="linked-interaction-title"
            >
              {displayTitle} | {displayDate} | Duration: {formattedDuration}
            </Typography>
          </Box>
          {props.canEdit && 
          <Box className="linked-interaction-action-icons">
            <Tooltip
              title="Edit"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  className: "linked-interaction-tooltip",
                },
                arrow: { className: "linked-interaction-tooltip-arrow" },
              }}
            >
              <IconButton
                onClick={() => setIsEditing(true)}
                size="small"
                className="linked-interaction-edit-btn"
              >
                <Box
                  component="img"
                  src={editIcon}
                  alt="edit"
                  className="linked-interaction-action-icon"
                />
              </IconButton>
            </Tooltip>

            <Tooltip
              title="Delete"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  className: "linked-interaction-tooltip",
                },
                arrow: { className: "linked-interaction-tooltip-arrow" },
              }}
            >
              <IconButton
                size="small"
                className="linked-interaction-delete-btn"
                onClick={() => {
                  selfSelectedIdRef.current = "";
                  props.onSelectInteraction("", {
                    interactionTitle: null,
                    startTime: null,
                    duration: null,
                  });
                  setSelectedInteraction(null);
                }}
              >
                <Box
                  component="img"
                  src={deleteIcon}
                  alt="delete"
                  className="linked-interaction-action-icon"
                />
              </IconButton>
            </Tooltip>
          </Box>
          }
        </Box>
      ) : (
        /* ---------- EDIT/SEARCH MODE ---------- */
        <Box className="linked-interaction-edit-mode">
          <TextField
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="linked-interaction-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    src={searchIcon}
                    alt="search"
                    className="linked-interaction-search-icon"
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box className="linked-interaction-search-controls">
                    {(isLoading || isFetching) && (
                      <CircularProgress
                        size={16}
                        thickness={5}
                        className="linked-interaction-loading"
                      />
                    )}

                    {search && !(isLoading || isFetching) && (
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        className="linked-interaction-clear-btn"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}

                    <Box className="linked-interaction-week-picker">
                      <IconButton
                        size="small"
                        onClick={goToPreviousWeek}
                        className="linked-interaction-week-btn"
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>

                      <CalendarTodayIcon className="linked-interaction-calendar-icon" />

                      <Typography className="linked-interaction-week-range">
                        {formatDate(weekStart)} - {formatDate(weekEnd)}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={goToNextWeek}
                        disabled={isCurrentWeek}
                        className="linked-interaction-week-btn"
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </InputAdornment>
              ),
            }}
          />

          {isSearching && isRequestFinished && (
            <Paper
              elevation={4}
              className="linked-interaction-results"
            >
              {interactions.length > 0 ? (
                <List dense>
                  {interactions.map((item: any) => (
                    <ListItemButton
                      key={item.interactionId || item.id || item.interaction_id}
                      onClick={() => handleClick(item)}
                      className="linked-interaction-result-item"
                    >
                      <ListItemText
                        primary={`${
                          item.interactionTitle ||
                          item.title ||
                          item.interaction_title ||
                          "Untitled"
                        } | ${formatDisplayDate(
                          item.startTime || item.start_time || item.meetingStartTime
                        )} | Duration: ${formatDuration(
                          item.duration ??
                            item.durationInMinutes ??
                            item.meetingDuration ??
                            item.duration_minutes
                        )}`}
                        primaryTypographyProps={{
                          className: "linked-interaction-result-text",
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box className="linked-interaction-empty">
                  <Typography className="linked-interaction-empty-text">
                    No interactions found for "{debouncedSearch}"
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LinkedInteraction;
